import { Component, ElementRef, inject, OnDestroy, ViewChild } from '@angular/core';

import { RouterModule } from '@angular/router';
import { MenuItem } from 'primeng/api';
import { Subscription } from 'rxjs';
import { AppMenuitem } from './app.menuitem';
import { AuthService } from '@/services/auth.service';
import { TrpcService } from '@/services/trpc.service';
import { FeatureFlagService } from '@/services/feature-flag.service';
import { CommonService } from '@/services/common.service';
import { ReconciliationAlertService, ReconciliationAlert } from '@/services/reconciliation-alert.service';

@Component({
    selector: '[app-menu]',
    standalone: true,
    imports: [AppMenuitem, RouterModule],
    template: `<div class="layout-menu-container" #menuContainer>
        <ul class="layout-menu">
            @for (item of model; track item; let i = $index) {

                @if (!item.separator) {
<li app-menuitem [item]="item" [index]="i" [root]="true"></li>
}
                @if (item.separator) {
<li class="menu-separator"></li>
}

}
        </ul>
    </div>`
})
export class AppMenu implements OnDestroy {
    el: ElementRef = inject(ElementRef);

    @ViewChild('menuContainer') menuContainer!: ElementRef;
    model: MenuItem[] = [];

    private reconciliationAlertSubscription?: Subscription;

    adminMenuItems: MenuItem[] = [
        {
            label: 'Dashboard',
            icon: 'pi pi-home',
            routerLink: ['/admin/dashboard']
        }, {
            label: 'User Login Management',
            icon: 'pi pi-key',
            routerLink: ['/admin/manage-logins']
        }, {
            label: '',
            icon: 'pi pi-users',
            items: [
                {
                    label: 'All Tenants',
                    icon: 'pi pi-list',
                    routerLink: ['/admin/manage-tenants']
                },
                {
                    label: 'Pending Invitations',
                    icon: 'pi pi-envelope',
                    routerLink: ['/admin/pending-invitations']
                },
                {
                    label: 'Account Requests',
                    icon: 'pi pi-user-plus',
                    routerLink: ['/admin/account-requests']
                }
            ]
        }, {
            label: 'Lease Management',
            icon: 'pi pi-book',
            routerLink: ['/admin/manage-leases']
        }, {
            label: 'Suite Management',
            icon: 'pi pi-address-book',
            routerLink: ['/admin/manage-suites']
        }, {
            label: 'Monthly Billing',
            icon: 'pi pi-dollar',
            routerLink: ['/admin/monthly-billing']
        }, {
            label: 'View Invoices',
            icon: 'pi pi-receipt',
            routerLink: ['/admin/invoice-list']
        }, {
            label: 'Invoice Tenant',
            icon: 'pi pi-file-edit',
            routerLink: ['/admin/invoice-tenant']
        }, {
            label: 'Receive Payment',
            icon: 'pi pi-wallet',
            routerLink: ['/admin/receive-payment']
        }, {
            label: '',
            icon: 'pi pi-history',
            items: [
                {
                    label: 'Payment Transactions',
                    icon: 'pi pi-file-excel',
                    routerLink: ['/admin/payment-logs']
                },
                {
                    label: 'Bank Deposit',
                    icon: 'pi pi-building-columns',
                    routerLink: ['/admin/bank-deposit']
                },
                {
                    label: 'AutoPay Logs',
                    icon: 'pi pi-clock',
                    routerLink: ['/admin/autopay-logs']
                },
                {
                    label: 'Tenant Activity',
                    icon: 'pi pi-history',
                    routerLink: ['/admin/tenant-activity']
                },
                {
                    label: 'Invoice Billing Report',
                    icon: 'pi pi-chart-bar',
                    routerLink: ['/admin/invoice-billing-report']
                },
                {
                    label: 'Monthly Billing History',
                    icon: 'pi pi-calendar',
                    routerLink: ['/admin/monthly-billing-history']
                },
                {
                    label: 'Pending Payments Report',
                    icon: 'pi pi-clipboard',
                    routerLink: ['/admin/pending-payments-report']
                },
                {
                    label: 'Aged Receivables Report',
                    icon: 'pi pi-calendar-times',
                    routerLink: ['/admin/aged-receivables-report']
                }
            ]
        }, {
            label: '',
            icon: 'pi pi-cog',
            items: [
                {
                    label: 'Global Settings',
                    icon: 'pi pi-sliders-h',
                    routerLink: ['/admin/settings']
                },
                {
                    label: 'Property Settings',
                    icon: 'pi pi-building',
                    routerLink: ['/admin/property-settings']
                },
                {
                    label: 'System Status',
                    icon: 'pi pi-heart',
                    routerLink: ['/admin/system-status']
                },
                {
                    label: 'Email Logs',
                    icon: 'pi pi-envelope',
                    routerLink: ['/admin/email-logs']
                },
                {
                    label: 'Charge Code Management',
                    icon: 'pi pi-tags',
                    routerLink: ['/admin/charge-codes']
                },
                {
                    label: 'Rate Bump Preview',
                    icon: 'pi pi-chart-line',
                    routerLink: ['/admin/dev/rate-bump-preview']
                }
            ]
        }, {
            label: 'Link Tenant To ACH Deposit Account',
            icon: 'pi pi-link',
            routerLink: ['/admin/link-tenant-to-ach-deposit-account']
        }, {
            label: 'ACH Deposit Reconciliation',
            icon: 'pi pi-dollar',
            routerLink: ['/admin/ach-deposit-reconciliation']
        }
    ];

    tenantMenuItems: MenuItem[] = [
        {
            label: 'Dashboard',
            icon: 'pi pi-home',
            routerLink: ['/tenant/dashboard']
        }, {
            label: 'View Invoices',
            icon: 'pi pi-receipt',
            routerLink: ['tenant/invoice-list']
        }, {
            label: 'Payment Transactions',
            icon: 'pi pi-dollar',
            routerLink: ['/tenant/payment-transactions']
        }, {
            label: 'Email Notifications',
            icon: 'pi pi-envelope',
            routerLink: ['/tenant/email-notifications']
        }, {
            label: 'Leases',
            icon: 'pi pi-file-pdf',
            routerLink: ['/tenant/lease-documents']
        }
    ];

    accountingMenuItems: MenuItem[] = [
        {
            label: 'Dashboard',
            icon: 'pi pi-home',
            routerLink: ['/accounting/dashboard']
        },
        {
            label: 'View Invoices',
            icon: 'pi pi-receipt',
            routerLink: ['/accounting/invoice-list']
        },
        {
            label: 'Post Charges',
            icon: 'pi pi-money-bill',
            routerLink: ['/accounting/dashboard']
        },
        {
            label: 'Accounting Activity',
            icon: 'pi pi-save',
            routerLink: ['/accounting/accounting-activity']
        },
        {
            label: 'Generate Report',
            icon: 'pi pi-clipboard',
            routerLink: ['/accounting/dashboard']
        },
        {
            label: 'Invoice Tenant',
            icon: 'pi pi-file-edit',
            routerLink: ['/accounting/invoice-tenant']
        },
        {
            label: 'Charge Code Management',
            icon: 'pi pi-tags',
            routerLink: ['/accounting/charge-codes']
        },
        {
            label: 'Receive Payment',
            icon: 'pi pi-wallet',
            routerLink: ['/accounting/receive-payment']
        },
        {
            label: 'ACH Deposit Reconciliation',
            icon: 'pi pi-dollar',
            routerLink: ['/accounting/ach-deposit-reconciliation']
        },{
            label: 'Email Logs',
            icon: 'pi pi-envelope',
            routerLink: ['/accounting/email-logs']
        }
    ];

    developerMenuItems: MenuItem[] = [
        {
            label: '',
            icon: 'pi pi-code',
            items: [
                {
                    label: 'Developer Settings',
                    icon: 'pi pi-cog',
                    routerLink: ['/admin/dev/settings']
                },
                {
                    label: 'AcceptBlue Customers',
                    icon: 'pi pi-users',
                    routerLink: ['/admin/dev/accept-blue-customers']
                },
                {
                    label: 'Aged Receivables',
                    icon: 'pi pi-chart-bar',
                    routerLink: ['/admin/dev/aged-receivables']
                },
                {
                    label: 'Medici Ledger',
                    icon: 'pi pi-book',
                    routerLink: ['/admin/dev/medici-ledger']
                },
                {
                    label: 'Invoice Ledger',
                    icon: 'pi pi-list',
                    routerLink: ['/admin/dev/ledger']
                },
                {
                    label: 'Credit Reconciliation',
                    icon: 'pi pi-sync',
                    routerLink: ['/admin/dev/credit-balance-reconciliation']
                },
                {
                    label: 'Billing Preview',
                    icon: 'pi pi-calculator',
                    routerLink: ['/admin/dev/monthly-billing']
                }
            ]
        }
    ];

    constructor(
        private authService: AuthService,
        private trpcService: TrpcService,
        private featureFlagService: FeatureFlagService,
        private commonService: CommonService,
        private reconciliationAlertService: ReconciliationAlertService
    ) {


    }

    async ngOnInit() {

        let currentUser = this.authService.getCurrentUser();

        // Filter menu items based on feature flags
        const filterMenuItems = (items: MenuItem[]): MenuItem[] => {
            if (this.featureFlagService.isAchEnabled()) {
                return items; // Show all items if ACH is enabled
            }

            // Filter out ACH-related menu items
            return items.filter(item => {
                const routerLink = item.routerLink?.[0];
                return routerLink !== '/admin/link-tenant-to-ach-deposit-account' &&
                       routerLink !== '/admin/ach-deposit-reconciliation' &&
                       routerLink !== '/accounting/ach-deposit-reconciliation';
            });
        };

        if (currentUser?.role == "admin") {
            this.model = filterMenuItems(this.adminMenuItems);

            // Add developer menu if feature flag is enabled
            if (this.featureFlagService.isDevMenuEnabled()) {
                this.model.push(...this.developerMenuItems);
            }
        } else if (currentUser?.role == "tenant") {
            this.model = this.tenantMenuItems;
            await this.loadSettings();
        } else if (currentUser?.role == "accounting") {
            this.model = filterMenuItems(this.accountingMenuItems);
        }

        this.model.push({
            label: 'Logout',
            icon: 'pi pi-sign-out',
            routerLink: ['/logout']
        });

        // Wire the "System Status" badge to the reconciliation alert (admin only).
        // Mutating the MenuItem's `badge`/`badgeStyleClass` fields in place is
        // enough — primeng re-reads them on next change detection tick.
        if (currentUser?.role === 'admin') {
            this.reconciliationAlertSubscription = this.reconciliationAlertService.alert$
                .subscribe((alert) => this.applyReconciliationBadge(alert));
        }
    }

    /**
     * Find the "System Status" menu item (nested one level deep) and mutate
     * its badge fields based on the current alert. Runs on every alert$ emit.
     */
    private applyReconciliationBadge(alert: ReconciliationAlert | null): void {
        const item = this.findMenuItemByRoute('/admin/system-status');
        if (!item) return;
        if (alert && alert.needsAlert) {
            // discrepancies → numeric count, stale/failed/never-run → '!'.
            item.badge = alert.reason === 'discrepancies'
                ? String(alert.flaggedCount)
                : '!';
            item.badgeStyleClass = 'p-badge-danger';
        } else {
            delete item.badge;
            delete item.badgeStyleClass;
        }
    }

    /** Recursive MenuItem lookup by routerLink. Only looks one level deep since
     *  that's the codebase convention, but the recursion protects against
     *  future menu reorganizations. */
    private findMenuItemByRoute(route: string): MenuItem | null {
        const walk = (items: MenuItem[] | undefined): MenuItem | null => {
            if (!items) return null;
            for (const item of items) {
                if (Array.isArray(item.routerLink) && item.routerLink[0] === route) {
                    return item;
                }
                const child = walk(item.items);
                if (child) return child;
            }
            return null;
        };
        return walk(this.model);
    }

    ngOnDestroy() {
        if (this.reconciliationAlertSubscription) {
            this.reconciliationAlertSubscription.unsubscribe();
        }
    }
    async loadSettings() {
        try {
            const globalSettingResult = await this.trpcService.admin.getGlobalSettings.query();

            const isPaymentEnabled = this.commonService.toBooleanSafe(globalSettingResult?.['payment_enabled']);
            const isAutopayEnabled = this.commonService.toBooleanSafe(globalSettingResult?.['autopay_enabled']);
            if(isPaymentEnabled) {
                if (isAutopayEnabled) {
                    this.model.push({
                        label: 'Autopay',
                        icon: 'pi pi-refresh',
                        routerLink: ['/tenant/autopay']
                    });
                }
                this.model.push({
                    label: 'Payment Method Management',
                    icon: 'pi pi-credit-card',
                    routerLink: ['/tenant/manage-payment-methods']
                });
            }

        } catch (error: any) {
            console.error('Failed to load settings');
        }
    }
}
